import React, { useCallback, useEffect, useRef, useState } from 'react';
import { log } from 'log';
import { prop } from 'ramda';
import uuid from 'uuid-random';
import { useForm } from 'react-final-form';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';

import { Control } from '../Control';
import withField from '../withField';
import ValidationIcon from '../Icons/ValidationIcon';
import VisibilityIcon from '../Icons/VisibilityIcon';
import { InputShallow } from './components/InputShallow';

const InputBare = ({
    /* rff render props */
    input,
    meta,

    /* user provided props */
    bottomHint,
    className = '',
    classNames,
    currencySymbol,
    escapeSymbolsRegExp,
    hint,
    id,
    infoTooltip,
    inputRef: customInputRef,
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,
    label,
    name,
    onClick,
    onMouseDown,
    placeholder = '',
    readOnly,
    type = 'text',
    ...inputDomProps
}) => {
    const formAPI = useForm();
    const focusRef = useRef(false);
    const idRef = useRef(id || uuid());
    const defaultInputRef = useRef();
    const inputRef = customInputRef || defaultInputRef;
    const translate = useTranslations('playtech.mobile.system.input.');
    const { active, touched, error, validating, submitting } = meta;
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const showPasswordIcon = isEnablePasswordSpy && type === 'password';
    const [isVisible, setIsVisible] = useState(false);
    const toggleVisible = () => setIsVisible(!isVisible);
    const dataCurrencyAttribute = currencySymbol ? { 'data-currency-symbol': currencySymbol } : {};
    const preventClick = useCallback(event => event.preventDefault(), []);

    const onInput = useCallback((event) => {
        if (escapeSymbolsRegExp) {
            try {
                event.target.value = event.target.value.replace(new RegExp(escapeSymbolsRegExp), '');
            } catch (err) {
                log('error', err);
            }
        }

        input.onInput?.(event);
    }, [escapeSymbolsRegExp, input]);

    useEffect(() => {
        if (!name) {
            return;
        }

        const onFocusInHandler = (event) => {
            if (event.target === inputRef.current) {
                focusRef.current = true;
            }
        };
        const onBlurHandler = (event) => {
            if (event.target !== inputRef.current || active) {
                return;
            }

            if (focusRef.current) {
                focusRef.current = false;

                return;
            }

            // A native blur (not focusout) event without previous focusin event occurs on browser autofill.
            // We need to trigger RFF validation via API blur or resetFieldState methods.
            // blur - will highlight field according to validation results
            // resetFieldState - will provide silent validation.
            if (touched) {
                formAPI.blur(name);
            } else {
                formAPI.resetFieldState(name);
            }
        };

        document.body.addEventListener('focusin', onFocusInHandler);
        document.body.addEventListener('blur', onBlurHandler, true);

        return () => {
            document.body.removeEventListener('focusin', onFocusInHandler);
            document.body.removeEventListener('blur', onBlurHandler, true);
        };
    }, [active, formAPI, inputRef, name, touched]);

    return (
        <Control {...{
            id: idRef.current,
            meta,
            readOnly,
            className,
            classNames,
            onClick,
            label,
            hint,
            bottomHint,
            animate: true,
            infoTooltip,
            isLabelAsteriskEnabled,
            ...dataCurrencyAttribute
        }}>
            <InputShallow
                {...input}
                {...inputDomProps}
                id={idRef.current}
                name={name}
                readOnly={readOnly}
                tabIndex={readOnly ? -1 : 0}
                type={showPasswordIcon && isVisible ? 'text' : type}
                aria-label={inputDomProps['aria-label'] || translate('playtech.mobile.system.input.aria-label')}
                {...(submitting || validating) && { disabled: true }}
                onMouseDown={readOnly ? preventClick : onMouseDown}
                placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                {...escapeSymbolsRegExp ? { onInput } : {}}
                {...inputRef ? { ref: inputRef } : {}}
            />

            {touched && !active && !validating && !readOnly && (
                <ValidationIcon
                    animate={true}
                    isValid={!error}
                    aria-label={error}
                    tabIndex={error ? 0 : -1}
                    className={classNames?.validationIcon}
                />
            )}

            {showPasswordIcon && (
                <VisibilityIcon
                    onClick={toggleVisible}
                    isVisible={isVisible}
                    aria-label={isVisible
                        ? translate('password.aria-label.hide')
                        : translate('password.aria-label.show')
                    }
                />
            )}
        </Control>
    );
};

export const Input = withField(InputBare);
